import { enhancedApi as eligibilityGeneratedApi } from '@store/generated/eligibility';
import { enhancedApi } from '@store/generated/patient-coverage';

/**
 * Response from the insurance card extraction endpoint.
 * Fields map 1:1 to PatientCoverage model.
 */
export type InsuranceCardExtractionResponse = {
  payer_name: string | null;
  member_id: string | null;
  group_number: string | null;
  plan_name: string | null;
  subscriber_first_name: string | null;
  subscriber_last_name: string | null;
  subscriber_relationship: string | null;
  effective_date: string | null;
  card_image_url_front: string | null;
  card_image_url_back: string | null;
  confidence: number;
};

/**
 * Feature-specific tag types for cache management
 */
const PATIENT_COVERAGE_TAG_TYPES = ['PatientCoverage', 'PatientCoverageList', 'PayerStats'] as const;

/**
 * Patient Coverages API - extends the generated OpenAPI client
 * This ensures we're using generated types and endpoints
 * while maintaining feature-specific customization
 */
export const patientCoveragesApi = enhancedApi.enhanceEndpoints({
  addTagTypes: PATIENT_COVERAGE_TAG_TYPES,
  endpoints: {
    // Payer stats (org-wide summary)
    getV2ProviderPatientsPayerStats: {
      providesTags: ['PayerStats'],
    },

    // Enhance generated endpoints with cache tags
    getV2ProviderPatientsByPatientIdCoverages: {
      providesTags: (result, _error, arg) =>
        result
          ? [
              ...result.coverages.map(coverage => ({
                type: 'PatientCoverage' as const,
                id: coverage.patient_insurance_coverage_id,
              })),
              { type: 'PatientCoverageList' as const, id: arg.patientId },
            ]
          : [{ type: 'PatientCoverageList', id: arg.patientId }],
    },

    postV2ProviderPatientsByPatientIdCoverages: {
      invalidatesTags: (_result, _error, arg) => [
        { type: 'PatientCoverageList', id: arg.patientId },
        'PayerStats',
      ],
    },

    getV2ProviderPatientsByPatientIdCoveragesAndCoverageId: {
      providesTags: (_result, _error, arg) => [
        { type: 'PatientCoverage', id: arg.coverageId },
      ],
    },

    putV2ProviderPatientsByPatientIdCoveragesAndCoverageId: {
      invalidatesTags: (_result, _error, arg) => [
        { type: 'PatientCoverage', id: arg.coverageId },
        { type: 'PatientCoverageList', id: arg.patientId },
        'PayerStats',
      ],
    },

    deleteV2ProviderPatientsByPatientIdCoveragesAndCoverageId: {
      invalidatesTags: (_result, _error, arg) => [
        { type: 'PatientCoverage', id: arg.coverageId },
        { type: 'PatientCoverageList', id: arg.patientId },
        'PayerStats',
      ],
    },
  },
});

// Custom (non-generated) endpoints for insurance card upload
const insuranceCardApi = patientCoveragesApi.injectEndpoints({
  endpoints: builder => ({
    uploadInsuranceCard: builder.mutation<
      InsuranceCardExtractionResponse,
      { patientId: string; files: File[] }
    >({
      query: ({ patientId, files }) => {
        const formData = new FormData();
        files.forEach(file => formData.append('files[]', file, file.name));
        return {
          url: `/v2/provider/patients/${patientId}/insurance-card`,
          method: 'POST',
          body: formData,
        };
      },
    }),
  }),
  overrideExisting: false,
});

export const { useUploadInsuranceCardMutation } = insuranceCardApi;

// Export ONLY user-friendly hook names (never export verbose names)
export const {
  useGetV2ProviderPatientsPayerStatsQuery: useGetPayerStatsQuery,
  useGetV2ProviderPatientsByPatientIdCoveragesQuery: useGetPatientCoveragesQuery,
  useLazyGetV2ProviderPatientsByPatientIdCoveragesQuery: useLazyGetPatientCoveragesQuery,
  usePostV2ProviderPatientsByPatientIdCoveragesMutation: useCreatePatientCoverageMutation,
  useGetV2ProviderPatientsByPatientIdCoveragesAndCoverageIdQuery: useGetPatientCoverageByIdQuery,
  useLazyGetV2ProviderPatientsByPatientIdCoveragesAndCoverageIdQuery:
    useLazyGetPatientCoverageByIdQuery,
  usePutV2ProviderPatientsByPatientIdCoveragesAndCoverageIdMutation:
    useUpdatePatientCoverageMutation,
  useDeleteV2ProviderPatientsByPatientIdCoveragesAndCoverageIdMutation:
    useDeletePatientCoverageMutation,
} = patientCoveragesApi;

// ── Eligibility API (generated from OpenAPI) ──────────────────────────

export const eligibilityApi = eligibilityGeneratedApi.enhanceEndpoints({
  addTagTypes: ['Eligibility', 'BatchProgress'],
  endpoints: {
    // Encounter-scoped
    getV2ProviderEncountersByEncounterIdEligibility: {
      providesTags: (_result, _error, { encounterId }) => [
        { type: 'Eligibility' as const, id: encounterId },
      ],
    },
    postV2ProviderEncountersByEncounterIdEligibility: {
      invalidatesTags: (_result, _error, { encounterId }) => [
        { type: 'Eligibility' as const, id: encounterId },
      ],
    },
    postV2ProviderEncountersByEncounterIdEligibilityOverride: {
      invalidatesTags: (_result, _error, { encounterId }) => [
        { type: 'Eligibility' as const, id: encounterId },
      ],
    },
    // Patient-scoped
    getV2ProviderPatientsByPatientIdEligibility: {
      providesTags: (_result, _error, { patientId }) => [
        { type: 'Eligibility' as const, id: `patient-${patientId}` },
      ],
    },
    postV2ProviderPatientsByPatientIdEligibility: {
      invalidatesTags: (_result, _error, { patientId }) => [
        { type: 'Eligibility' as const, id: `patient-${patientId}` },
      ],
    },
    // Batch eligibility
    postV2ProviderEligibilityBatch: {
      invalidatesTags: ['BatchProgress'],
    },
    getV2ProviderEligibilityBatchByBatchId: {
      providesTags: (_result, _error, { batchId }) => [
        { type: 'BatchProgress' as const, id: batchId },
      ],
    },
  },
});

export const {
  // Encounter-scoped
  useGetV2ProviderEncountersByEncounterIdEligibilityQuery: useGetEncounterEligibilityQuery,
  usePostV2ProviderEncountersByEncounterIdEligibilityMutation: useRunEligibilityCheckMutation,
  usePostV2ProviderEncountersByEncounterIdEligibilityOverrideMutation:
    useOverrideEligibilityMutation,
  // Patient-scoped
  useGetV2ProviderPatientsByPatientIdEligibilityQuery: useGetPatientEligibilityQuery,
  usePostV2ProviderPatientsByPatientIdEligibilityMutation: useRunPatientEligibilityCheckMutation,
  // Batch eligibility
  usePostV2ProviderEligibilityBatchMutation: useRunBatchEligibilityMutation,
  useGetV2ProviderEligibilityBatchByBatchIdQuery: useGetBatchProgressQuery,
} = eligibilityApi;
